import type { TFunction } from 'i18next';
import type { SupportedLocale } from '../i18n';
import { formatPublicOrderReference, isolateOrderReferenceForLocale } from '../lib/order-reference';

/**
 * F-022 — Pure link builder for the admin "Contact customer" CTA.
 *
 * LOAD-BEARING LANDMINE — `wa.me` vs `tel:` leading-`+` handling.
 * Mirrors `build-contact-link.ts` exactly. A silent regression here
 * is UNDETECTABLE BY EYE because both URL schemes render as valid-looking
 * strings but deliver the wrong recipient.
 *
 *   - `wa.me/<e164-without-plus>` — STRIP the leading `+`.
 *   - `tel:<e164-with-plus>`       — PRESERVE the leading `+`.
 *
 * Concrete pinning example (the unit tests in `build-order-contact-link.test.ts`
 * assert both forms with hardcoded `expect(url).toBe('...')` assertions —
 * not `.toContain(...)` partial matches — so any regression that
 * converges the two forms fails loudly at test time):
 *
 *   input number '+972501234567' →
 *     whatsapp → 'https://wa.me/972501234567?text=<encodeURIComponent(msg)>'
 *     phone    → 'tel:+972501234567'
 *
 * Security — W1 (E.164 runtime guard):
 * Before constructing ANY URL, the number is validated against
 * E164_REGEX = /^\+\d{7,15}$/. If the check fails, returns `null` and
 * logs '[buildOrderContactLink] invalid phone number format'. The caller
 * (order-detail-customer.tsx) MUST handle null by hiding the contact button.
 * This prevents a malformed value (e.g. a `javascript:` scheme injection) from
 * ever reaching an <a href>.
 *
 * Pure module — no DOM, no React, no `window` access.
 */

/** E.164 canonical format: leading `+` followed by 7–15 digits. */
const E164_REGEX = /^\+\d{7,15}$/;

type OrderContactMethod = 'whatsapp' | 'phone';
type OrderContactMessageVariant = 'admin' | 'storefront';

interface BuildOrderContactLinkBaseInput {
  readonly method: OrderContactMethod;
  /** E.164 canonical number (leading `+`). Validated at function level. */
  readonly number: string;
  readonly orderId: string;
  /** Diagnostic — the `t` parameter handles the actual locale lookup. */
  readonly locale: SupportedLocale;
  /**
   * Must be the live `useTranslation().t` reference from the calling component.
   * The function wraps the call in try/catch — on throw the fallback message
   * (containing orderId) is used and a console.error is logged.
   */
  readonly t: TFunction;
}

export type BuildOrderContactLinkInput =
  | (BuildOrderContactLinkBaseInput & {
      readonly messageVariant?: Extract<OrderContactMessageVariant, 'admin'>;
    })
  | (BuildOrderContactLinkBaseInput & {
      readonly messageVariant: Extract<OrderContactMessageVariant, 'storefront'>;
      readonly storeName: string;
    });

/**
 * Build the final `wa.me` or `tel:` URL for the admin contact-customer CTA.
 * Pure, synchronous, heavily tested.
 *
 * Returns `null` when the phone number fails E.164 validation — callers
 * must hide the contact button in that case. This is the primary security
 * gate (W1) preventing XSS via `javascript:` scheme injection.
 */
export function buildOrderContactLink(input: BuildOrderContactLinkInput): string | null {
  // W1: E.164 runtime guard — validate before constructing any URL.
  if (!E164_REGEX.test(input.number)) {
    console.error('[buildOrderContactLink] invalid phone number format');
    return null;
  }

  if (input.method === 'phone') {
    // tel: — PRESERVE the leading `+`. No query-string body.
    return `tel:${input.number}`;
  }

  // whatsapp branch — build pre-filled message first.
  const orderReference = isolateOrderReferenceForLocale(
    formatPublicOrderReference(input.orderId),
    input.locale,
  );
  let message: string;
  try {
    if (input.messageVariant === 'storefront') {
      message = input.t('order.contactMerchantWhatsappMessage', {
        storeName: input.storeName,
        orderId: orderReference,
        interpolation: { escapeValue: false },
      });
    } else {
      message = input.t('admin.orders.detail.contactWhatsappMessage', {
        orderId: orderReference,
        interpolation: { escapeValue: false },
      });
    }
  } catch (err) {
    // F-022-build-order-contact-link-throw-guard: t() threw at runtime --
    // fall back to a minimal untranslated message so the wa.me URL is
    // still functional. Log so the regression surfaces in dev tools.
    console.error('[buildOrderContactLink] t() threw, using fallback message', err);
    message =
      input.messageVariant === 'storefront'
        ? `Hello ${input.storeName}, I'd like to ask about order ${orderReference}`
        : `Hello, I'd like to discuss order ${orderReference}`;
  }

  // wa.me — STRIP leading `+`. The E164_REGEX check above guarantees
  // there is exactly one leading `+`, so a single character-strip is sufficient.
  const digits = input.number.replace(/^\+/, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}
